/**
 * Gesetzliche Feiertage in Deutschland je Bundesland – selbst berechnet (Osterformel), ohne externen Dienst.
 * Nur landesweite Feiertage; regional begrenzte (z. B. Mariä Himmelfahrt in katholischen Gemeinden Bayerns,
 * Fronleichnam in Teilen Sachsens/Thüringens, Augsburger Friedensfest) bewusst nicht – lieber einer zu wenig als falsch.
 */
export const DE_STATES = {
  "DE-BW": "Baden-Württemberg",
  "DE-BY": "Bayern",
  "DE-BE": "Berlin",
  "DE-BB": "Brandenburg",
  "DE-HB": "Bremen",
  "DE-HH": "Hamburg",
  "DE-HE": "Hessen",
  "DE-MV": "Mecklenburg-Vorpommern",
  "DE-NI": "Niedersachsen",
  "DE-NW": "Nordrhein-Westfalen",
  "DE-RP": "Rheinland-Pfalz",
  "DE-SL": "Saarland",
  "DE-SN": "Sachsen",
  "DE-ST": "Sachsen-Anhalt",
  "DE-SH": "Schleswig-Holstein",
  "DE-TH": "Thüringen",
} as const;
export type DeState = keyof typeof DE_STATES;
export const isDeState = (v: unknown): v is DeState => typeof v === "string" && v in DE_STATES;

export interface Holiday {
  /** YYYY-MM-DD */
  date: string;
  name: string;
  /** bundesweit (sonst nur in diesem Bundesland) */
  national: boolean;
}

/** Ostersonntag (gregorianisch, Gauß/Meeus) als UTC-Datum */
export function easterSunday(year: number): Date {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

const ymd = (d: Date) => d.toISOString().slice(0, 10);
const plus = (d: Date, days: number) => new Date(d.getTime() + days * 86_400_000);
const fixed = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d));

/** Feiertage eines Jahres in einem Bundesland, nach Datum sortiert */
export function stateHolidays(year: number, state: DeState): Holiday[] {
  const e = easterSunday(year);
  const only = (...states: DeState[]) => states.includes(state);
  const list: [Date, string, boolean][] = [
    [fixed(year, 1, 1), "Neujahr", true],
    [plus(e, -2), "Karfreitag", true],
    [plus(e, 1), "Ostermontag", true],
    [fixed(year, 5, 1), "Tag der Arbeit", true],
    [plus(e, 39), "Christi Himmelfahrt", true],
    [plus(e, 50), "Pfingstmontag", true],
    [fixed(year, 10, 3), "Tag der Deutschen Einheit", true],
    [fixed(year, 12, 25), "1. Weihnachtstag", true],
    [fixed(year, 12, 26), "2. Weihnachtstag", true],
  ];
  if (only("DE-BW", "DE-BY", "DE-ST")) list.push([fixed(year, 1, 6), "Heilige Drei Könige", false]);
  if ((state === "DE-BE" && year >= 2019) || (state === "DE-MV" && year >= 2023)) list.push([fixed(year, 3, 8), "Internationaler Frauentag", false]);
  if (only("DE-BB")) list.push([e, "Ostersonntag", false], [plus(e, 49), "Pfingstsonntag", false]);
  if (only("DE-BW", "DE-BY", "DE-HE", "DE-NW", "DE-RP", "DE-SL")) list.push([plus(e, 60), "Fronleichnam", false]);
  if (only("DE-SL")) list.push([fixed(year, 8, 15), "Mariä Himmelfahrt", false]);
  if (state === "DE-TH" && year >= 2019) list.push([fixed(year, 9, 20), "Weltkindertag", false]);
  if (only("DE-BB", "DE-MV", "DE-SN", "DE-ST", "DE-TH") || (only("DE-HB", "DE-HH", "DE-NI", "DE-SH") && year >= 2018) || year === 2017)
    list.push([fixed(year, 10, 31), "Reformationstag", year === 2017]);
  if (only("DE-BW", "DE-BY", "DE-NW", "DE-RP", "DE-SL")) list.push([fixed(year, 11, 1), "Allerheiligen", false]);
  if (only("DE-SN")) {
    // Mittwoch vor dem 23. November
    const nov23 = fixed(year, 11, 23);
    const back = ((nov23.getUTCDay() - 3 + 7) % 7) || 7;
    list.push([plus(nov23, -back), "Buß- und Bettag", false]);
  }
  return list.map(([d, name, national]) => ({ date: ymd(d), name, national })).sort((a, b) => a.date.localeCompare(b.date));
}

/** Feiertage in einem Zeitraum (YYYY-MM-DD, einschließlich) */
export function stateHolidaysBetween(from: string, to: string, state: DeState): Holiday[] {
  const out: Holiday[] = [];
  for (let y = Number(from.slice(0, 4)); y <= Number(to.slice(0, 4)); y++) out.push(...stateHolidays(y, state).filter((h) => h.date >= from && h.date <= to));
  return out;
}

/**
 * Bundesland aus der PLZ (Leitregion; bei abweichenden Bereichen 3-stellig). Postleitzahlen folgen nicht exakt den
 * Landesgrenzen – für Orte an der Grenze lässt sich das Bundesland in den Standort-Einstellungen festlegen.
 */
const ZIP3: Record<string, DeState> = {
  "275": "DE-HB", "276": "DE-HB", "364": "DE-TH", "365": "DE-TH", "637": "DE-BY", "638": "DE-BY", "639": "DE-BY",
  "667": "DE-RP", "668": "DE-RP", "669": "DE-RP", "193": "DE-BB", "172": "DE-BB", "210": "DE-HH", "211": "DE-HH",
};
const ZIP2: Record<string, DeState> = {
  "01": "DE-SN", "02": "DE-SN", "03": "DE-BB", "04": "DE-SN", "06": "DE-ST", "07": "DE-TH", "08": "DE-SN", "09": "DE-SN",
  "10": "DE-BE", "11": "DE-BE", "12": "DE-BE", "13": "DE-BE", "14": "DE-BB", "15": "DE-BB", "16": "DE-BB", "17": "DE-MV", "18": "DE-MV", "19": "DE-MV",
  "20": "DE-HH", "21": "DE-NI", "22": "DE-HH", "23": "DE-SH", "24": "DE-SH", "25": "DE-SH", "26": "DE-NI", "27": "DE-NI", "28": "DE-HB", "29": "DE-NI",
  "30": "DE-NI", "31": "DE-NI", "32": "DE-NW", "33": "DE-NW", "34": "DE-HE", "35": "DE-HE", "36": "DE-HE", "37": "DE-NI", "38": "DE-NI", "39": "DE-ST",
  "40": "DE-NW", "41": "DE-NW", "42": "DE-NW", "44": "DE-NW", "45": "DE-NW", "46": "DE-NW", "47": "DE-NW", "48": "DE-NW", "49": "DE-NI",
  "50": "DE-NW", "51": "DE-NW", "52": "DE-NW", "53": "DE-NW", "54": "DE-RP", "55": "DE-RP", "56": "DE-RP", "57": "DE-NW", "58": "DE-NW", "59": "DE-NW",
  "60": "DE-HE", "61": "DE-HE", "63": "DE-HE", "64": "DE-HE", "65": "DE-HE", "66": "DE-SL", "67": "DE-RP", "68": "DE-BW", "69": "DE-BW",
  "70": "DE-BW", "71": "DE-BW", "72": "DE-BW", "73": "DE-BW", "74": "DE-BW", "75": "DE-BW", "76": "DE-BW", "77": "DE-BW", "78": "DE-BW", "79": "DE-BW",
  "80": "DE-BY", "81": "DE-BY", "82": "DE-BY", "83": "DE-BY", "84": "DE-BY", "85": "DE-BY", "86": "DE-BY", "87": "DE-BY", "88": "DE-BW", "89": "DE-BW",
  "90": "DE-BY", "91": "DE-BY", "92": "DE-BY", "93": "DE-BY", "94": "DE-BY", "95": "DE-BY", "96": "DE-BY", "97": "DE-BY", "98": "DE-TH", "99": "DE-TH",
};
export function stateFromZip(zip: string | null | undefined, country: string | null | undefined = "DE"): DeState | null {
  if ((country ?? "DE").toUpperCase() !== "DE") return null;
  const z = (zip ?? "").replace(/\D/g, "");
  if (z.length !== 5) return null;
  return ZIP3[z.slice(0, 3)] ?? ZIP2[z.slice(0, 2)] ?? null;
}
